import _ from 'lodash';
import Numeral from 'numeral';
import { createSelector } from 'reselect';
import { RecruitmentActivityType } from '../../../../../shared/types/recruitmentTypes';
import { RAID_METADATA_BY_SLUG, Expansions, DIFFICULTY_SLUGS_DESCENDING } from '../../../shared/enums';
import { getScheduleHighlights } from '../../../../../shared/frontend/scripts/recruitment/utils';
import { serializeRecruitmentProfile } from '../../../../../shared/frontend/scripts/recruitment/utils';
import { allRaidsSelector } from '../../../../../shared/frontend/scripts/selectors/global';
import moment from 'moment';
import { TRecruitmentGuildProfile } from '../libs/store';
import { getRaidWeekForRaidAndRegion } from '../../../../../shared/common/utils';
import { getRequirementHighlights } from '../shared/recruitment/utils';
/*
 * guild details:
 */
const guildDetailsState = (st: any) => st?.guildDetails;

export const guildIdSelector = createSelector(guildDetailsState, ({ group }) => group?.id);

/*
 * guild recruitment page
 */

const guildRecruitmentPageStateSelector = (st: any) => st.guildRecruitment;
const _applicationStateSelector = createSelector(guildRecruitmentPageStateSelector, ({ application }) => application);

export const applicationModalVisibilitySelector = createSelector(
  guildRecruitmentPageStateSelector,
  ({ applicationModalOpen }) => applicationModalOpen
);
export const applicationErrorMsgSelector = createSelector(
  guildRecruitmentPageStateSelector,
  ({ errorMessage }) => errorMessage
);
export const threadIdSelector = createSelector(_applicationStateSelector, ({ threadId }) => threadId);
export const applicationStatusSelector = createSelector(_applicationStateSelector, ({ loading, loaded, error }) => ({
  loading,
  loaded,
  error
}));

export const guildRecruitmentProfilePageStatusSelector = createSelector(
  guildRecruitmentPageStateSelector,
  ({ loading, loaded, error }) => ({
    loading,
    loaded,
    error
  })
);

// note: this is taken from guildPageContainer where the highlights are built for
// guild masthead. If we keep these raid highlights the same, this should be moved
// and then let guildPageContainer decided how it wants to process the results
// from this
const buildRaidingHighlights = (details: any, allRaids: any) =>
  _.compact(
    _.flatten(
      allRaids.map((raid: any, index: number) => {
        const matchedRaidProgress = _.filter(details?.raidProgress, { raid: raid.slug });
        if (matchedRaidProgress.length === 0) {
          // no progress for this raid
          return [];
        }

        // only refer to current expansion raids
        if (raid.expansion_id !== Expansions.Current) {
          return [];
        }

        const difficulties = [];
        const [raidProgress] = matchedRaidProgress;

        for (const diff of DIFFICULTY_SLUGS_DESCENDING) {
          if (raidProgress.encountersDefeated[diff].length > 0) {
            difficulties.push(diff);
          }
        }

        if (difficulties.length === 0) {
          // has not killed anything for this raid
          return [];
        }

        if (difficulties.length > 1) {
          if (raidProgress.encountersDefeated[difficulties[0]].length === raid.encounters.length) {
            difficulties.length = 1;
          }
        }

        const raidWeekAotC = getRaidWeekForRaidAndRegion(
          raid.slug,
          details.guild.region.slug,
          'heroic',
          moment.utc(raidProgress.aotc)
        );

        const raidWeekCuttingEdge = getRaidWeekForRaidAndRegion(
          raid.slug,
          details.guild.region.slug,
          'mythic',
          moment.utc(raidProgress.cuttingEdge)
        );

        // show the top 2 difficulties only
        return difficulties.map((difficulty: string) => {
          const raidStat = {
            key: `${difficulty}-${raid.slug}`,
            numDefeated: raidProgress.encountersDefeated[difficulty].length,
            totalBosses: raid.encounters.length,
            difficulty,
            raidName: raid.name,
            aotc: raidProgress.aotc && difficulty === 'heroic',
            aotcEarned: `(Earned ${Numeral(raidWeekAotC).format('O,o')} Raid Week)`,
            ce: raidProgress.ce && difficulty === 'mythic',
            ceEarned: `(Earned ${Numeral(raidWeekCuttingEdge).format('O,o')} Raid Week)`,
            raidShortName: RAID_METADATA_BY_SLUG[raid.slug]?.short_name
          };
          return {
            label: raidStat.raidShortName,
            value: `${raidStat.numDefeated}/${raidStat.totalBosses}`,
            difficulty: raidStat.difficulty
          };
        });
      })
    )
  );

// note: make these builders be able to be shared between guild and team
// move to shared/recruitment/utils when that happens. For now its just guild
// specific(and team is not set up)
const getProgressHighlights = (profile: TRecruitmentGuildProfile, details: any, allRaids: any) => {
  switch (profile.activity_type) {
    case RecruitmentActivityType.GuildRaids:
    case RecruitmentActivityType.GuildPvp:
    case RecruitmentActivityType.GuildSocial: {
      return buildRaidingHighlights(details, allRaids);
    }
    default: {
      return [];
    }
  }
};

export const guildRecruitmentProfilesSelector = createSelector(
  guildRecruitmentPageStateSelector,
  allRaidsSelector,
  ({ profiles }, allRaids) =>
    _.map(profiles || [], (p) => {
      const profile = serializeRecruitmentProfile({}, p);
      return {
        profile,
        progressHighlights: getProgressHighlights(profile, p.details, allRaids),
        requirementHighlights: getRequirementHighlights(profile),
        scheduleHighlights: getScheduleHighlights(profile)
      };
    })
);

// the profile for which the user wants to apply with
export const sourceProfileIdSelector = createSelector(
  guildRecruitmentPageStateSelector,
  ({ sourceProfileId }) => sourceProfileId
);
export const targetProfileIdSelector = createSelector(
  guildRecruitmentPageStateSelector,
  ({ targetProfileId }) => targetProfileId
);
export const applicationMessageSelector = createSelector(guildRecruitmentPageStateSelector, ({ message }) => message);

// const x = (st) => st;
export const applicationIdsSelector = createSelector(
  // x,
  sourceProfileIdSelector,
  targetProfileIdSelector,
  applicationMessageSelector,
  (/* aaa */ sourceProfileId, targetProfileId, message) => {
    // console.log({ aaa })
    return message ? { targetProfileId, sourceProfileId, message } : { targetProfileId, sourceProfileId };
  }
);

export const targetProfileSelector = createSelector(
  targetProfileIdSelector,
  guildRecruitmentProfilesSelector,
  (targetProfileId, profiles) =>
    targetProfileId > 0 ? _.find(profiles, (p) => p.profile.id === targetProfileId) : undefined
);
